/**
 * BoardDataTool - Separated from BoardsThunks to avoid circular dependency
 *
 * This file is imported by:
 * - @common/tools/index.ts (centralized tools export)
 * - TaskThunks.ts (which BoardsThunks imports from)
 *
 * By keeping this in a separate file, we break the circular dependency:
 * BoardsThunks → TaskThunks → BoardDataTool (no cycle)
 */

import { BoardView } from "@common-models/board-views";
import { isExtensionContext } from "@common-services/detect-context";
import { CommonRootState } from "@common/types/common-root-state-type";
import { createAsyncThunk } from "@reduxjs/toolkit";
import {
    boardViewSelectByQuery,
    DBBoardUserThunks,
    DBFormThunks,
    DBGanttThunks,
    DBKanbanThunks,
    DBNotificationsThunks,
    DBTaskColumnOptionThunks,
    DBTaskColumnThunks,
    DBTaskgroupsThunks,
    DBTasksExtraDataFilesThunks,
    DBTasksExtraDataThunks,
    DBTasksThunks,
    DBTimeTrackingSessionsThunks,
    DBViewFiltersThunks,
    DBWorkloadThunks,
    TaskColumnOptionReducerActions,
    taskgroupsReducerActions,
    taskGroupsSelectByQueryOnlyLength,
    tasksReducerActions,
} from "@common-reducers/DBServiceThunks";
import { taskActions } from "./TasksReducer";


/**
 * Boards whose full data set has been fetched at least once in THIS browser
 * session. Module-level (not persisted): resets on hard refresh / logout, which
 * is exactly the desired behavior — a fresh page load always re-fetches.
 *
 * Switching back to a board already in this set skips the destructive
 * removeAll() + full unpaginated refetch, so cached rows render instantly with
 * no empty-board flash and no redundant network round-trip. Cached data stays
 * live while the user is away via MQTT entity-update events (create/patch/remove
 * in NotiferHandlers.ts), so trusting the cache on revisit is safe.
 */
const fullyLoadedBoardIds = new Set<string>();

export const getBoardDataTool = createAsyncThunk<Promise<any>, { boardId: string; lean?: boolean }, { state: CommonRootState }>(
    "getBoardDataTool",
    async ({ boardId, lean = false }, thunkApi) => {
        const { dispatch } = thunkApi;
        const state: CommonRootState = thunkApi.getState();
        const currentBoardView = state.TasksReducer.currentBoardView;
        const [boardView]: BoardView[] = boardViewSelectByQuery(state.DBBoardViewReducer, { boardId: boardId, _id: currentBoardView.id });
        const viewId = boardView?._id ?? boardId
        const promiseList: Promise<unknown>[] = [];
        const isExtension = isExtensionContext();
        const userId = state.UserReducer.userId;

        dispatch(taskActions.clearDeletedBoardName())

        // Fast path: this board was already fully loaded this session AND its
        // groups are still present in the store (guards against eviction /
        // CONTENT_CAP retention purges). Skip the removeAll()+refetch entirely —
        // board-scoped selectors (baseTasks/currentGroups filter by
        // selectedBoardId) mean other boards' cached rows never leak into this
        // board's view, so leaving the store populated is safe. MQTT keeps the
        // cached rows fresh while away.
        const groupsInStore = taskGroupsSelectByQueryOnlyLength(state.DBTaskgroupsReducer, {
            boardId,
            deleted: false,
            archived: { $ne: true },
        });
        if (fullyLoadedBoardIds.has(boardId) && groupsInStore > 0) {
            return Promise.resolve([]);
        }

        dispatch(tasksReducerActions.removeAll())
        dispatch(taskgroupsReducerActions.removeAll())
        dispatch(TaskColumnOptionReducerActions.removeAll())



        promiseList.push(dispatch(
            DBBoardUserThunks.find({
                boardId,
                deleted: false,
                $paginate: false,
                returnAlsoBoardUserNameAndPorfileImg: true,
            })
        ));

        promiseList.push(dispatch(
            DBTaskgroupsThunks.find({
                boardId,
                deleted: false,
                archived: { $ne: true },
                $paginate: false,
            })
        ));

        promiseList.push(dispatch(
            DBTasksThunks.find({
                boardId,
                deleted: false,
                archived: { $ne: true },
                $paginate: false,
            })
        ));

        promiseList.push(dispatch(
            DBTaskColumnThunks.find({
                boardId,
                deleted: false,
                $paginate: false,
            })
        ));

        promiseList.push(dispatch(
            DBTaskColumnOptionThunks.find({
                boardId,
                deleted: false,
                $paginate: false,
            })
        ));

        if (!isExtension) {
            promiseList.push(dispatch(
                DBTasksExtraDataThunks.find({
                    boardId,
                    deleted: false,
                    $paginate: false,
                })
            ));


            promiseList.push(dispatch(
                DBTasksExtraDataFilesThunks.find({
                    boardId,
                    deleted: false,
                    $paginate: false,
                })
            ));

            // Alternate board VIEWS (kanban/gantt/form/workload/time-tracking) and
            // their view-filters are never rendered by the campaign audience
            // editor — it only shows the contacts table (groups + tasks + columns).
            // In `lean` mode (opened from a campaign) skip them so a board open
            // fires a handful of targeted requests instead of the full fan-out.
            if (!lean) {
                promiseList.push(dispatch(
                    DBKanbanThunks.find({
                        boardId,
                        deleted: false,
                        $paginate: false,
                    })
                ));
                promiseList.push(dispatch(
                    DBGanttThunks.find({
                        boardId,
                        deleted: false,
                        $paginate: false,
                    })
                ));

                promiseList.push(dispatch(
                    DBFormThunks.find({
                        boardId,
                        deleted: false,
                        $paginate: false,
                    })
                ));

                promiseList.push(dispatch(
                    DBWorkloadThunks.find({
                        boardId,
                        deleted: false,
                        $paginate: false,
                    })
                ));

                promiseList.push(dispatch(
                    DBTimeTrackingSessionsThunks.find({
                        boardId,
                        deleted: false,
                        $paginate: false,
                    })
                ));

                promiseList.push(dispatch(
                    DBViewFiltersThunks.find({
                        viewId,
                        deleted: false,
                    })
                ));
            }
        }

        // Board notification badges — not shown in the campaign audience editor.
        if (!lean) {
            promiseList.push(dispatch(
                DBNotificationsThunks.find({
                    owner: userId,
                    deleted: false,
                })
            ))
        }
        const results = await Promise.all(promiseList);
        // Mark this board as fully loaded so subsequent switches back to it take
        // the fast path above — but ONLY after a full (non-lean) load. A lean open
        // skips the alternate views, so marking it would let a later full open
        // early-return with those views never fetched.
        if (!lean) {
            fullyLoadedBoardIds.add(boardId);
        }
        return results;
    }
);
